import { Box3, Mesh, Scene, Vector3 } from "three";
import {DataProjectionCoordinate, SceneCoordinate} from "../projections";
import {Viewer} from "../main";
import { isEqual } from 'lodash';
import { Asset, TagAnnotation, TagData, TagItemData } from '../../../redux/assets-slice';
import { mergeBoundingBoxes } from '../utilities';
import { AnnotationDrawType, AnnotationSize, TagSize } from '../../../types/tags';
import { SceneCameraState } from '../../../redux/camera-slice';
import { CustomMouseEvent } from '../controls';
import { AerialTags, SceneViewTags, TagAnnotations } from '.';
import { MapBrowserEvent } from "ol";

// TODO: have a mesh and layer per TagFile

export class TagMesh extends Mesh {
  public tags;

  constructor(geometry, material, tags) {
    super(geometry, material);
    this.tags = tags;
  };
};

class TagPosition {
  private _scene: SceneCoordinate;
  private _aerial: number[];

  private input: Vector3;
  private tag: TagItem;

  constructor(tag: TagItem, position) {
    this.tag = tag;
    this.input = new Vector3(...position);
  }

  get array() {
    return this.tag.isAerial
      ? [this.input.x, this.input.y]
      : [this.input.x, this.input.y, this.input.z];
  }

  get scene() {
    if (!this._scene) {
      const value = new DataProjectionCoordinate(this.input).toScene();
      this._scene = value;
    }

    return this._scene;
  }

  get aerial() {
    if (!this._aerial) {
      const value = new DataProjectionCoordinate(this.input).toAerial();
      this._aerial = value;
    }

    return this._aerial;
  }

  reset() {
    this._scene = null;
    this._aerial = null;
  }
}

export class TagItem {
  public id: string;
  public csv: string;
  public name: string;
  public file: TagFile;
  public position: TagPosition;
  public comment: string;
  public annotations: TagAnnotation[];
  public sceneState: SceneCameraState;
  public isAerial = false;
  public texture: string;
  public visible: boolean;
  public size: number;

  constructor(asset: TagFile, tag: TagItemData) {
    this.id = tag.id;
    this.file = asset;
    this.name = tag.name;
    this.comment = tag.comment;
    this.annotations = tag.annotations;
    this.sceneState = tag.sceneState;
    this.isAerial = !("z" in tag);

    this.csv = asset.id;
    this.texture = asset.texture;
    this.visible = asset.visible;
    this.size = asset.size;

    const position = [tag.x, tag.y, tag.z];
    this.position = new TagPosition(this, position);
  }
}

export class TagFile {
  private tagsHandler: TagsHandler;
  public id: string;
  public saved: boolean;
  public size: TagSize;
  public texture: string;
  public visible: boolean;
  public name: string;
  public tags: TagItem[] = [];

  constructor(tagsHandler: TagsHandler, asset: Asset) {
    this.tagsHandler = tagsHandler;

    this.id = asset.id;
    this.saved = asset.saved;
    this.name = asset.name;
  }

  get viewer() {
    return this.tagsHandler.viewer;
  }

  get sceneViewTags() {
    return this.tags.filter(tag => !tag.isAerial);
  }

  zoomToExtent() {
    if (this.tags.length === 0) return;
    const boundingBox = boundingBoxForTags(this.tags);
    this.viewer.zoomToSceneExtent(boundingBox, false);
  }

  update(asset: Asset) {
    const {size, texture, items} = asset.data as TagData;

    const visibleChanged = this.visible !== asset.visible;
    this.visible = asset.visible;

    const textureChanged = this.texture !== texture;
    this.texture = texture;

    const sizeChanged = this.size !== size;
    this.size = size;

    const newTagIDs = items.map(x => x.id);
    const currentTagIDs = this.tags.map(x => x.id);
    const tagsChanged = !isEqual(newTagIDs, currentTagIDs);

    // Update all tags (comments may have changed)
    this.tags = items.map(tag => {
      return new TagItem(this, tag);
    });

    if (!this.saved) {
      this.zoomToExtent();
    }

    return visibleChanged
      || textureChanged
      || tagsChanged
      || sizeChanged;
  }
}

export class TagsHandler {
  public viewer: Viewer;
  private sceneViewController: SceneViewTags;
  private aerialController: AerialTags;
  private annotations: TagAnnotations;
  public tagState = {};
  private files: TagFile[] = [];

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.sceneViewController = new SceneViewTags(this);
    this.aerialController = new AerialTags(this);
    this.annotations = new TagAnnotations(this);
  }

  get hovering() {
    return this.aerialController.hovering;
  }

  get scene(): Scene {
    return this.sceneViewController.scene;
  }

  get tags(): TagItem[] {
    return this.files.map(file => file.tags).flat();
  }

  get combinedBoundingBox() {
    return boundingBoxForTags(this.tags);
  }

  public get numberOfFiles() {
    return this.files.length;
  }

  addNewFile(asset: Asset) {
    if (this.getByID(asset.id)) return;

    const file = new TagFile(this, asset);
    this.files.push(file);
  }

  deleteFile(file: TagFile) {
    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
  }

  updateTagData = (assetID: string, asset) => {
    let file = this.getByID(assetID);
    if (!file) return false;

    return file.update(asset);
  }

  getTooltipText(tagID: string): string[] {
    const selected = this.tags.find(x => x.id === tagID);
    if (!selected) return;

    const fileName = selected.file.name;
    const tagName = selected.name;

    return [`[${fileName}]`, tagName];
  }

  getByID(assetID: string) {
    return this.files.find(file => file.id === assetID);
  }

  getTagByID(tagID: string) {
    const allTags = this.files
      .map(file => file.tags)
      .flat();

    return allTags.find(tag => tag.id === tagID);
  }

  getTagByQuery(query: string) {
    if (!query) return;

    const allTags = this.files
      .map(file => file.tags)
      .flat();

    const search = query.toLowerCase();

    return allTags.find(tag => {
      const name = tag.name.toLowerCase();
      const comment = tag.comment.toLowerCase();

      return (tag.id === search)
        || (name.includes(search))
        || (comment.includes(search));
    });
  }

  getRowsCSV(tags: TagItem[]) {
    let tagCsvOut = [];

    const hasHeight = tags.filter(tag => !tag.isAerial).length > 0;
    const hasComments = tags.filter(tag => tag.comment).length > 0;
    const hasAnnotations = tags.filter(tag => tag.annotations.length > 0).length > 0;

    const header = [
      'Name',
      'Easting',
      'Northing',
      ...hasHeight ? ['Elevation'] : [],
      ...hasComments ? ['Comments'] : [],
      ...hasAnnotations ? ['Annotations'] : []
    ];

    tagCsvOut.push(header);

    tags.forEach(tag => {
      let annotations = "";

      if (tag.annotations.length > 0) {
        let annotation = tag.annotations
          .map(annotation => annotation.definition);
        annotations = JSON.stringify(annotation);
      }

      const row = [
        tag.name,
        ...tag.position.array,
        ...hasComments ? [tag.comment] : [],
        ...hasAnnotations ? [annotations] : []
      ];
      tagCsvOut.push(row);
    });

    return tagCsvOut;
  }

  setMaxDistance(distance: number) {
    let value = (distance > 999) ? 1e10 : distance;
    this.sceneViewController.setMaxDistance(value);
  }

  reconcile(tagFiles: Asset[]) {
    const currentIDs = new Set(this.files.map(x => x.id));
    const newAssetIDs = new Set(tagFiles.map(x => x.id));
    const assetsToLoad = tagFiles.filter(x => !currentIDs.has(x.id));
    const filesToDelete = this.files.filter(x => !newAssetIDs.has(x.id));

    // Delete files that need to be removed
    filesToDelete.forEach(file => {
      this.deleteFile(file);
    });

    // Add new tag files
    assetsToLoad.forEach(file => {
      this.addNewFile(file);
    });

    let needsUpdate = filesToDelete.length > 0;
    tagFiles.forEach(file => {
      const updated = this.updateTagData(file.id, file);
      needsUpdate = needsUpdate || updated;
    });

    if (needsUpdate) {
      this.sceneViewController.reconcile(this.files);
      this.aerialController.reconcile(this.files);
    }

    return assetsToLoad.length;
  }

  toggleAnnotations(state: boolean) {
    this.annotations.setState(state);
  }

  loadAnnotations(annotations: TagAnnotation[]) {
    this.annotations.load(annotations);
  }

  getAnnotations() {
    return this.annotations.getAnnotations();
  }

  setAnnotationType(type: AnnotationDrawType) {
    this.annotations.setType(type);
  }

  setAnnotationDrawing(drawing: boolean) {
    this.annotations.setDrawing(drawing);
  }

  setAnnotationColor(color: string) {
    this.annotations.setColor(color);
  }

  setAnnotationSize(size: AnnotationSize) {
    this.annotations.setSize(size);
  }

  setSelected(tagID: string, timeoutSeconds: number) {
    //this.aerialController.setSelected(tagID, timeoutSeconds);
    this.sceneViewController.setSelected(tagID, timeoutSeconds);
  }

  onMouseMoveScene(event: CustomMouseEvent) {
    this.sceneViewController.onMouseMove(event);
  }

  onMouseMoveAerial(event: MapBrowserEvent<UIEvent>) {
    if (this.annotations.drawing) {
      this.annotations.onMouseMove();
    } else {
      this.aerialController.onMouseMove(event);
    }
  }

  onMouseUpScene(event: CustomMouseEvent) {
    this.sceneViewController.onMouseUp(event);
  }

  onMouseUpAerial(event: CustomMouseEvent) {
    return this.annotations.onMouseUp(event);
  }

  onMapClick(event) {
    this.annotations.onMapClick(event);
  }

  getPickerResults(event) {
    return this.sceneViewController.getPickerResults(event);
  }

  getFeatureForEvent(event) {
    return this.aerialController.getFeatureForEvent(event);
  }

  update() {
    this.sceneViewController.update();
  }
}

export const boundingBoxForTags = (tags: TagItem[]) => {
  const buffer = 2.0;

  const boundingBoxes = tags.map(tag => {
    if (tag.isAerial) return null;

    const position = tag.position.scene;
    return new Box3(
      new Vector3().copy(position).subScalar(buffer),
      new Vector3().copy(position).addScalar(buffer)
    );
  });

  return mergeBoundingBoxes(boundingBoxes);
};